import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import {
  Snackbar,
  Alert,
  AlertColor,
  Slide,
  SlideProps,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import { X } from "lucide-react";
import { ZINDEX } from "@/design-system/tokens";

// Default auto-hide durations when a call site doesn't pass its own durationMs —
// toasts with an action get longer, since the user needs time to notice and click it.
const DEFAULT_DURATION_MS = 5000;
const DEFAULT_ACTION_DURATION_MS = 8000;

const SEVERITY_STYLES: Record<
  AlertColor,
  { accent: string; bg: string; border: string }
> = {
  success: { accent: "#2E7D32", bg: "#E8F5E9", border: "rgba(46,125,50,0.15)" },
  error: { accent: "#C62828", bg: "#FFEBEE", border: "rgba(198,40,40,0.15)" },
  warning: { accent: "#E65100", bg: "#FFF3E0", border: "rgba(230,81,0,0.15)" },
  info: { accent: "#1565C0", bg: "#E3F2FD", border: "rgba(21,101,192,0.15)" },
};

function SlideFromRight(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

interface SnackbarAction {
  label: string;
  onClick: () => void;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
  action?: SnackbarAction;
  durationMs?: number;
  // Mirrors instanceKeyRef.current at the point showSnackbar last ran, so the
  // Snackbar's `key` prop can be read from state during render instead of
  // reading the ref directly (React: don't read ref.current while rendering).
  // instanceKeyRef itself stays separate — the action click handler needs a
  // value it can mutate and read back synchronously within one event-handler
  // call, which state updates can't do.
  instanceKey: number;
}

interface SnackbarContextType {
  showSnackbar: (
    message: string,
    severity?: AlertColor,
    action?: SnackbarAction,
    durationMs?: number
  ) => void;
  hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
    instanceKey: 0,
  });
  // Bumped on every showSnackbar call and mirrored into snackbarState.instanceKey
  // (used as the Snackbar's `key`), so a second trigger while one is already open
  // force-remounts it instead of silently updating props on the still-open instance
  // (MUI doesn't reliably reflect that — see
  // https://mui.com/material-ui/react-snackbar/#consecutive-snackbars).
  // This makes each new toast — and its own auto-hide timer — start fresh immediately.
  // Kept as a ref (rather than reading instanceKey from state) because the action
  // click handler below needs to mutate and read it back synchronously within a
  // single event-handler call, which a state update can't do.
  const instanceKeyRef = useRef(0);

  const showSnackbar = useCallback(
    (
      message: string,
      severity: AlertColor = "info",
      action?: SnackbarAction,
      durationMs?: number
    ) => {
      instanceKeyRef.current += 1;
      setSnackbarState({
        open: true,
        message,
        severity,
        action,
        durationMs,
        instanceKey: instanceKeyRef.current,
      });
    },
    []
  );

  const hideSnackbar = useCallback(() => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  }, []);

  const contextValue = useMemo(
    () => ({
      showSnackbar,
      hideSnackbar,
    }),
    [showSnackbar, hideSnackbar]
  );

  const severityStyle = SEVERITY_STYLES[snackbarState.severity];

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        key={snackbarState.instanceKey}
        open={snackbarState.open}
        autoHideDuration={
          snackbarState.durationMs ??
          (snackbarState.action
            ? DEFAULT_ACTION_DURATION_MS
            : DEFAULT_DURATION_MS)
        }
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={SlideFromRight}
        // Overrides MUI's default (1400), which sits below Radix dialogs/sheets.
        sx={{ zIndex: ZINDEX.snackbar }}
      >
        <Alert
          severity={snackbarState.severity}
          variant="standard"
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {snackbarState.action && (
                <Button
                  size="small"
                  onClick={() => {
                    const keyBeforeAction = instanceKeyRef.current;
                    snackbarState.action?.onClick();
                    // Only auto-dismiss if the action didn't already show a
                    // follow-up toast of its own (that bumps the instance key) —
                    // otherwise this would close the new toast the instant it opens.
                    if (instanceKeyRef.current === keyBeforeAction) {
                      hideSnackbar();
                    }
                  }}
                  sx={{
                    color: severityStyle.accent,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    minWidth: 0,
                    px: 1,
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                  }}
                >
                  {snackbarState.action.label}
                </Button>
              )}
              <IconButton
                size="small"
                onClick={hideSnackbar}
                sx={{
                  color: "inherit",
                  opacity: 0.5,
                  "&:hover": { opacity: 0.8 },
                }}
              >
                <X size={16} />
              </IconButton>
            </Box>
          }
          sx={{
            width: "100%",
            minWidth: 340,
            maxWidth: 500,
            borderRadius: "8px",
            backgroundColor: severityStyle.bg,
            border: `1px solid ${severityStyle.border}`,
            borderLeft: `4px solid ${severityStyle.accent}`,
            boxShadow: "0 4px 14px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)",
            fontSize: "0.8125rem",
            fontWeight: 500,
            lineHeight: 1.5,
            "& .MuiAlert-icon": {
              alignItems: "center",
              opacity: 0.9,
            },
            "& .MuiAlert-message": {
              padding: "6px 0",
            },
            "& .MuiAlert-action": {
              paddingTop: "2px",
              alignItems: "flex-start",
              "& .MuiIconButton-root": {
                color: "inherit",
                opacity: 0.5,
                "&:hover": { opacity: 0.8 },
              },
            },
          }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarContextType {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
}
